import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Landmark } from "lucide-react";
import {
  Button,
  DetailSection,
  EmptyState,
  ListCard,
  SkeletonList,
  SkeletonText,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
  Timestamp,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCalendarDate, formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import { tpsLoansApi, type TpsLoanAcceptanceStatus, type TpsLoanDetail, type TpsLoanSummary } from "../api/tpsLoans";
import { TpsCycleMirror } from "../components/TpsCycleMirror";

// TPS Servicing Portal PR-C — the servicer's read-only roster of loans
// assigned to it. Selecting a loan opens a read-only drill-in (terms /
// parties / amortization schedule) with per-loan accept / decline of the
// servicing handoff while the loan is still Pending. No React Query in the
// portal — load state is managed with plain useState, and a successful
// accept / decline re-runs the loaders the caller owns.

const ACCEPTANCE_TONE: Record<TpsLoanAcceptanceStatus, StatusTone> = {
  Pending: "warn",
  Accepted: "ok",
  Declined: "danger",
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export function TpsLoansTab() {
  const [rows, setRows] = useState<TpsLoanSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    const data = await tpsLoansApi.list();
    setRows(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsLoansApi.list();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your assigned loans."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (selectedId) {
    return (
      <TpsLoanDetailPanel
        loanId={selectedId}
        onBack={() => setSelectedId(null)}
        refreshList={loadList}
      />
    );
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }

  if (!rows) {
    return <SkeletonList rows={5} data-testid="tps-loans-loading" />;
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Landmark}
        title="No loans assigned yet"
        description="When Phoenix assigns a loan to you for servicing, it will appear here for you to review and accept."
      />
    );
  }

  return (
    <section aria-label="Assigned loans" className="flex flex-col gap-space-3">
      {rows.map((loan) => (
        <ListCard
          key={loan.loanId}
          data-testid={`tps-loan-row-${loan.loanId}`}
          title={loan.loanNumber}
          status={
            <StatusBadge tone={ACCEPTANCE_TONE[loan.acceptanceStatus]}>
              {loan.acceptanceStatus}
            </StatusBadge>
          }
          meta={
            <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
              <span>{loan.borrowerName}</span>
              <span aria-hidden>·</span>
              <span>{prettyAccountState(loan.accountState)}</span>
              <span aria-hidden>·</span>
              <span>{formatCurrency(loan.currentBalance)}</span>
            </span>
          }
          actions={
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setSelectedId(loan.loanId)}
              data-testid={`tps-loan-open-${loan.loanId}`}
            >
              View
            </Button>
          }
        />
      ))}
    </section>
  );
}

function TpsLoanDetailPanel({
  loanId,
  onBack,
  refreshList,
}: {
  loanId: string;
  onBack: () => void;
  refreshList: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [, setParams] = useSearchParams();
  const [detail, setDetail] = useState<TpsLoanDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  // PR-D — set when accept is blocked by the debt-collector-language gate so
  // the panel can render an inline notice + a deep link to that state's editor
  // (the toast is transient; this stays put until the next accept attempt).
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsLoansApi.get(loanId);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load this loan."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loanId]);

  async function handleAccept() {
    setSubmitting(true);
    setAcceptError(null);
    try {
      const updated = await tpsLoansApi.accept(loanId);
      setDetail(updated);
      await refreshList();
      toast.success("Loan accepted.");
    } catch (err) {
      const message = errorMessage(err, "Couldn't accept this loan. Try again.");
      // PR-D — a 400 here is the debt-collector-language gate. Keep the toast
      // AND surface an inline notice with a deep link to the state's editor.
      setAcceptError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  // PR-D — switch the portal to the Debt-collector language tab, deep-linked to
  // the blocked loan's borrower state (mirrors how the portal switches tabs:
  // set ?tab= + the tab's own param).
  function reviewDebtCollectorLanguage(state: string) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "debt-collector-language");
        next.set("state", state);
        return next;
      },
      { replace: true },
    );
  }

  async function handleDecline() {
    const reason = declineReason.trim();
    if (!reason) return;
    setSubmitting(true);
    try {
      const updated = await tpsLoansApi.decline(loanId, reason);
      setDetail(updated);
      await refreshList();
      setDeclineOpen(false);
      setDeclineReason("");
      toast.success("Loan declined.");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't decline this loan. Try again."));
    } finally {
      setSubmitting(false);
    }
  }

  const backButton = (
    <Button type="button" variant="text" size="sm" onClick={onBack} data-testid="tps-loan-back">
      <ArrowLeft size={16} aria-hidden />
      Back to loans
    </Button>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <p className="text-small text-status-error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <DetailSection>
          <SkeletonText lines={4} />
        </DetailSection>
      </div>
    );
  }

  const { summary } = detail;
  const isPending = summary.acceptanceStatus === "Pending";

  return (
    <div className="flex flex-col gap-space-4">
      <div className="flex items-center justify-between gap-space-3">
        {backButton}
        <StatusBadge tone={ACCEPTANCE_TONE[summary.acceptanceStatus]}>
          {summary.acceptanceStatus}
        </StatusBadge>
      </div>

      <div>
        <h2 className="m-0 font-display text-h3 font-bold text-onyx">{summary.loanNumber}</h2>
        <p className="mt-space-1 text-small text-gray-mid">
          {summary.borrowerName} · {prettyAccountState(summary.accountState)} ·{" "}
          {formatCurrency(summary.currentBalance)} outstanding
        </p>
      </div>

      <DetailSection title="Servicing handoff">
        {isPending ? (
          declineOpen ? (
            <div className="flex flex-col gap-space-3">
              <p className="m-0 text-small text-onyx">
                Let Phoenix know why you're declining this loan. This note is shared with the
                Phoenix team.
              </p>
              <Textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Reason for declining…"
                rows={3}
                disabled={submitting}
                data-testid="tps-loan-decline-reason"
                aria-label="Reason for declining"
              />
              <div className="flex flex-wrap gap-space-2">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => void handleDecline()}
                  disabled={submitting || declineReason.trim().length === 0}
                  loading={submitting}
                  data-testid="tps-loan-decline-confirm"
                >
                  Confirm decline
                </Button>
                <Button
                  type="button"
                  variant="text"
                  size="sm"
                  onClick={() => {
                    setDeclineOpen(false);
                    setDeclineReason("");
                  }}
                  disabled={submitting}
                  data-testid="tps-loan-decline-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-space-3">
              <p className="m-0 text-small text-onyx">
                Review the loan below, then accept the servicing handoff or decline it.
              </p>
              <div className="flex flex-wrap gap-space-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => void handleAccept()}
                  disabled={submitting}
                  loading={submitting}
                  data-testid="tps-loan-accept"
                >
                  Accept
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setDeclineOpen(true)}
                  disabled={submitting}
                  data-testid="tps-loan-decline"
                >
                  Decline
                </Button>
              </div>
              {acceptError && (
                <div
                  className="flex flex-col items-start gap-space-2 rounded-md border border-status-warning bg-status-warning/10 p-space-3"
                  role="alert"
                  data-testid="tps-loan-accept-error"
                >
                  <p className="m-0 text-small text-onyx">{acceptError}</p>
                  {detail.borrowerState && (
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => reviewDebtCollectorLanguage(detail.borrowerState!)}
                      data-testid="tps-loan-review-dcl"
                    >
                      Review debt-collector language for {detail.borrowerState}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )
        ) : summary.acceptanceStatus === "Accepted" ? (
          <p className="m-0 text-small text-status-success" data-testid="tps-loan-accepted">
            You accepted this loan
            {summary.acceptedAt ? (
              <>
                {" "}
                <Timestamp value={summary.acceptedAt} />
              </>
            ) : null}
            .
          </p>
        ) : (
          <div className="flex flex-col gap-space-1" data-testid="tps-loan-declined">
            <p className="m-0 text-small text-status-error">You declined this loan.</p>
            {summary.declineReason && (
              <p className="m-0 text-small text-gray-mid">Reason: {summary.declineReason}</p>
            )}
          </div>
        )}
      </DetailSection>

      {/* SP-2b — the monthly money-cycle mirror (bill → review → payment →
          payout), self-loading with its own month picker. */}
      <DetailSection title="This month's cycle">
        <TpsCycleMirror loanId={loanId} />
      </DetailSection>

      <DetailSection title="Loan terms">
        <dl className="grid grid-cols-1 gap-space-3 sm:grid-cols-2">
          <Term label="Original loan amount" value={formatCurrency(detail.originalLoanAmount)} />
          <Term label="Interest rate" value={`${(detail.interestRate * 100).toFixed(3)}%`} />
          <Term label="Term" value={`${detail.termMonths} months`} />
          <Term
            label="Maturity date"
            value={detail.maturityDate ? formatCalendarDate(detail.maturityDate) : "—"}
          />
          <Term
            label="First payment date"
            value={detail.firstPaymentDate ? formatCalendarDate(detail.firstPaymentDate) : "—"}
          />
          <Term label="Assigned" value={formatDateInTz(summary.assignedAt)} />
        </dl>
      </DetailSection>

      <DetailSection title="Parties">
        {detail.parties.length === 0 ? (
          <p className="m-0 text-small text-gray-mid">No parties on file.</p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Role</TH>
                <TH>Name</TH>
                <TH>Email</TH>
                <TH>Phone</TH>
              </TR>
            </THead>
            <TBody>
              {detail.parties.map((party, i) => (
                <TR key={`${party.role}-${i}`}>
                  <TD>{party.role}</TD>
                  <TD>{party.name}</TD>
                  <TD>{party.email ?? "—"}</TD>
                  <TD>{party.phone ?? "—"}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </DetailSection>

      <DetailSection title="Amortization schedule">
        {detail.schedule.length === 0 ? (
          <p className="m-0 text-small text-gray-mid">No schedule available.</p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>#</TH>
                <TH>Due date</TH>
                <TH className="text-right">Principal</TH>
                <TH className="text-right">Interest</TH>
                <TH className="text-right">Total</TH>
                <TH className="text-right">Balance after</TH>
              </TR>
            </THead>
            <TBody>
              {detail.schedule.map((row) => (
                <TR key={row.periodNumber}>
                  <TD>{row.periodNumber}</TD>
                  <TD>{formatCalendarDate(row.dueDate)}</TD>
                  <TD className="text-right">{formatCurrency(row.principal, { maximumFractionDigits: 2 })}</TD>
                  <TD className="text-right">{formatCurrency(row.interest, { maximumFractionDigits: 2 })}</TD>
                  <TD className="text-right">{formatCurrency(row.total, { maximumFractionDigits: 2 })}</TD>
                  <TD className="text-right">{formatCurrency(row.balanceAfter, { maximumFractionDigits: 2 })}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </DetailSection>
    </div>
  );
}

function Term({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-caption text-gray-mid">{label}</dt>
      <dd className="m-0 text-small text-onyx">{value}</dd>
    </div>
  );
}

function prettyAccountState(state: string): string {
  return state.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
